/**
 * App shell — JuiceProvider + IdleAttractorScope("app") + AtmosphereShell +
 * BroadcastChrome, with the wouter Switch inside. The shell (chrome + stadium
 * atmosphere CSS) paints instantly from the service worker; routes arrive as
 * choreography (KICKOFF §3.3).
 *
 * screen_viewed fires on every route change; session_started once per boot
 * (lib/platform). OfflineBanner rides navigator.onLine.
 */
import { Suspense, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import {
  AtmosphereShell,
  BroadcastChrome,
  OfflineBanner,
  Skeleton,
} from "@matchday/ui";
import { IdleAttractorScope, JuiceProvider, juice } from "@matchday/juice";
import Landing from "./routes/Landing";
import { LAZY_ROUTES } from "./routes";
import { emitSessionStarted, trackScreen, tracker } from "./lib/platform";

function useOnline(): { online: boolean; queued: number } {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [queued, setQueued] = useState(() => tracker.pending());

  useEffect(() => {
    const sync = () => {
      setOnline(navigator.onLine);
      setQueued(tracker.pending());
    };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  return { online, queued };
}

export default function App() {
  const [location] = useLocation();
  const { online, queued } = useOnline();

  // Once per boot, with source derived from the ENTRY url (deeplink/share/organic).
  useEffect(() => {
    void emitSessionStarted(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every route change is a screen view.
  useEffect(() => {
    trackScreen(location);
  }, [location]);

  return (
    <JuiceProvider service={juice}>
      <IdleAttractorScope name="app">
        <AtmosphereShell>
          <OfflineBanner visible={!online} queuedCount={queued} />
          <BroadcastChrome prizeAmount="R5,000" prizeLabel="Prize Pool" />
          <Suspense
            fallback={
              <div className="pl-route-fallback">
                <Skeleton variant="panel" clip="bevel" height={220} />
              </div>
            }
          >
            <Switch>
              <Route path="/" component={Landing} />
              {LAZY_ROUTES.map(({ path, component }) => (
                <Route key={path} path={path} component={component} />
              ))}
              <Route>
                {/* Unknown routes fall back to the landing surface — a magic
                    link with a mangled path must never dead-end (MATCH POINT). */}
                <Landing />
              </Route>
            </Switch>
          </Suspense>
        </AtmosphereShell>
      </IdleAttractorScope>
    </JuiceProvider>
  );
}
